import { useState, useEffect, useRef } from 'react';

const THRESHOLD = 60;
export const BUTTON_WIDTH = 80;
export interface SwipeButtons {
  element: JSX.Element;
  onPress: () => void;
  buttonWidth?: number;
}

export const useSwipeableCard = ({
  leftButton,
  rightButton,
}: {
  leftButton?: SwipeButtons;
  rightButton?: SwipeButtons;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [startX, setStartX] = useState<number | null>(null);
  const [translateX, setTranslateX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    setStartX(e.touches[0].clientX);
    setIsDragging(true);
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!isDragging || startX === null) return;
    const deltaX = e.touches[0].clientX - startX;
    if ((deltaX > 0 && leftButton) || (deltaX < 0 && rightButton)) {
      setTranslateX(deltaX);
    } else {
      setTranslateX(0);
      setIsDragging(false);
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
    if (translateX < -THRESHOLD) {
      setTranslateX(
        -(rightButton ? (rightButton?.buttonWidth ?? BUTTON_WIDTH) : 0)
      ); // Snap left for left action buttons
    } else if (translateX > BUTTON_WIDTH) {
      setTranslateX(leftButton ? (leftButton?.buttonWidth ?? BUTTON_WIDTH) : 0); // Snap left for left action buttons
    } else {
      setTranslateX(0); // Not enough swipe, snap back
    }
  };

  // Close card manually (after an action)
  const resetCard = () => setTranslateX(0);

  useEffect(() => {
    function handleClick(event: MouseEvent | TouchEvent) {
      // If ref has been set and the click is outside it
      if (
        event.target &&
        ref.current &&
        !ref.current.contains(event.target as Node)
      ) {
        resetCard();
      }
    }
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('touchstart', handleClick);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('touchstart', handleClick);
    };
  }, []);

  return {
    ref,
    translateX,
    resetCard,
    isDragging,
    handleTouchStart,
    handleTouchEnd,
    handleTouchMove,
  };
};
